import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  HiOutlineMail,
  HiOutlineSupport,
  HiOutlineExternalLink,
  HiOutlineChevronDown,
  HiOutlineQuestionMarkCircle,
} from 'react-icons/hi';
import { FaTelegramPlane } from 'react-icons/fa';
import { useWallet } from '../context/WalletContext';
import { SupportSkeleton } from '../components/Skeleton';
import PageHeader from '../components/PageHeader';

function telegramHref(value) {
  const raw = String(value || '').trim();
  const fallback = 'https://t.me/dynafi_support';
  if (!raw) return fallback;
  if (/^(https?:\/\/|tg:)/i.test(raw)) return raw;
  const hostPath = raw.replace(/^(https?:\/\/)?/i, '').replace(/^\/+/, '');
  if (/^(t\.me|telegram\.me|telegram\.dog)\//i.test(hostPath)) {
    return `https://${hostPath}`;
  }
  const handle = raw.replace(/^@/, '').replace(/^\/+/, '');
  if (/^[A-Za-z0-9_]{4,32}$/.test(handle)) {
    return `https://t.me/${handle}`;
  }
  return fallback;
}

const FAQS = [
  {
    q: 'What is Dynamic Future Defi?',
    a: 'Dynamic Future Defi is a BNB Smart Chain (BSC) dApp where you deposit USDT into a plan, earn returns into an available balance, and claim rewards as DFI to your connected wallet. You can also invite others and earn from direct upline activity when enabled by admin.',
  },
  {
    q: 'Which network and tokens do I need?',
    a: 'Use BNB Smart Chain (BEP-20). Deposits are paid in USDT. Claims send DFI to your wallet. Make sure your wallet is on the correct chain ID (usually 56) before buying or claiming.',
  },
  {
    q: 'How do I connect my wallet?',
    a: 'Tap Connect in the navbar and choose MetaMask, Trust Wallet, or another injected wallet. Inside Trust’s dApp browser, connection can start automatically. Approve the connection request in your wallet.',
  },
  {
    q: 'I am a new user — how do I register?',
    a: 'Connect a wallet that has never used Dynamic Future Defi. If login fails with “not registered”, enter a refer (upline) wallet address. Confirm the BNB amount shown in the popup with your wallet to finish registration. The refer address must already be a registered Dynamic Future Defi user.',
  },
  {
    q: 'What is a refer / upline wallet?',
    a: 'Your upline is the member who invited you. Their wallet is set when you register with an invite link or by typing their address. After registration, your upline cannot be changed.',
  },
  {
    q: 'How do invite links work?',
    a: 'Open Invite after connecting. Your link looks like /dashboard/0xYourAddress. Anyone who opens it and registers will use you as their upline. Share only your own link.',
  },
  {
    q: 'How do I buy a plan?',
    a: 'Go to Plans, enter (or pick) a USDT amount within the allowed range, then pay that amount to the deposit address shown for this purchase. After the deposit finishes, Dynamic Future Defi activates your plan.',
  },
  {
    q: 'What are the minimum and maximum deposits?',
    a: 'Min and max USDT amounts are set by the admin and shown on the Plans page (deposit range). Amounts outside that range will be rejected.',
  },
  {
    q: 'When do daily earnings start?',
    a: 'After your purchase is confirmed, daily returns are credited according to the platform schedule (typically a daily job). The daily percentage is shown on Plans and Dashboard and can be changed by admin.',
  },
  {
    q: 'Where do my earnings go?',
    a: 'Earnings credit your available (virtual) balance inside Dynamic Future Defi. They are not sent on-chain until you claim. Check Dashboard or Wallet for the current available amount.',
  },
  {
    q: 'How do I claim DFI?',
    a: 'Open Wallet, review your available balance and unclaimed DFI estimate, then claim all or enter a custom amount. A claim charge (default 10%, set in admin) is taken from the amount before DFI is sent. Your wallet must be connected, and the amount must meet the minimum claim set by admin. Approve the request if your wallet prompts you.',
  },
  {
    q: 'Why did my claim fail?',
    a: 'Common causes: amount below the minimum claim, insufficient available balance, wrong network, rejected wallet prompt, or treasury not configured / low DFI on the server side. Check the claim status and error message on the Wallet page, then contact support with the time and wallet address.',
  },
  {
    q: 'What is the 2× earn cap?',
    a: 'Lifetime income from daily returns and team commissions cannot exceed a multiple of your total invested USDT (default 2×, admin-managed). When the cap is reached, further income is skipped. See Income for progress, remaining room, and skip reasons.',
  },
  {
    q: 'How does ROI upline commission work?',
    a: 'When someone below you receives daily ROI, you receive that level’s percent if you have unlocked it. Level 1 is open. See Level for your progress and Commission for the rates. You also need a minimum personal investment, shown on the Level page.',
  },
  {
    q: 'How does team / referral commission work?',
    a: 'When someone you directly invited buys a plan, a percentage of that purchase can be credited to you (default ~5%, set in admin). Deeper levels may appear on the Team page for structure, but commission rules follow current platform settings.',
  },
  {
    q: 'How does the referral pool work?',
    a: 'At the end of each day, a percent of that day’s confirmed deposits is split among the top 5 referrers by their direct users’ invested volume. Place percents are set in admin. Direct upline commission is still credited when a deposit confirms.',
  },
  {
    q: 'Where can I see my downline?',
    a: 'Open Team in the sidebar. Members are grouped by level: Level 1 joined with your invite; deeper levels joined under your team. You can filter by level and see invested amounts.',
  },
  {
    q: 'How does rank work?',
    a: 'Everyone starts at Rank 0. Higher ranks use confirmed USDT deposits in your downline, with no depth limit. The largest direct leg is the strong team and every other direct leg combined is the other team. Your qualifying amount is the smaller of those two. Explorer starts at 300 USDT matched business. Higher ranks can also require several direct teams, each with a set number of ranked users. Those counts are set by admin and shown on the Rank page. Crown Ambassador also needs 3 different legs that each have Elite Leader unlocked. Global Ambassador needs 3 different Ambassador legs, and DynaFi Legend needs 3 different Crown Ambassador legs.',
  },
  {
    q: 'When does my profile rank change, and when is the rank bonus paid?',
    a: 'The Rank page shows live team business immediately. The rank saved on your profile updates once a day at the time set in admin. After a rank is saved, that rank’s weekly bonus is credited on the weekday and time set in admin, until the max reward for that rank is reached. The bonus stays locked unless matching business grew by the admin growth percent over the admin day window. Weeks that do not qualify are held as a locked balance. On the next week that does qualify, the locked balance and that week’s bonus are paid to your balance together.',
  },
  {
    q: 'Why am I disconnected after refresh?',
    a: 'Dynamic Future Defi keeps your login in memory only for this browser session. Closing the tab or hard-refreshing signs you out. Connect again to continue — your on-chain funds and account data remain safe.',
  },
  {
    q: 'Can I use Trust Wallet mobile?',
    a: 'Yes. Open the site in Trust’s in-app dApp browser. The app can detect Trust and connect without showing the full wallet picker. Stay on BSC when depositing or claiming.',
  },
  {
    q: 'I paid USDT but my plan is not showing',
    a: 'Wait until the deposit finishes, then refresh Dashboard. Confirm you sent USDT (BEP-20) to the deposit address from this purchase and that the transfer succeeded on BscScan. If it still fails, send support your tx hash and wallet address.',
  },
  {
    q: 'Is my private key ever shared with Dynamic Future Defi?',
    a: 'No. You only sign transactions in your own wallet. Dynamic Future Defi never asks for your seed phrase or private key. Anyone asking for them is a scam — contact official support only via the links on this page.',
  },
  {
    q: 'How do I contact support?',
    a: 'Use Telegram or email from the cards above (addresses are managed by admin). Include your wallet address, what you tried, screenshots if useful, and any transaction hash. We typically reply within one business day.',
  },
];

function FaqItem({ item, open, onToggle }) {
  return (
    <div className={`faq-item${open ? ' is-open' : ''}`}>
      <button type="button" className="faq-item__q" onClick={onToggle} aria-expanded={open}>
        <span>{item.q}</span>
        <HiOutlineChevronDown className="faq-item__chevron" />
      </button>
      <AnimatePresence initial={false}>
        {open ? (
          <motion.div
            className="faq-item__a"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
          >
            <p>{item.a}</p>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}

export default function SupportPage() {
  const { loading, config } = useWallet();
  const [openFaq, setOpenFaq] = useState(0);

  const telegram = telegramHref(config?.support_telegram);
  const email = config?.support_email || 'support@dynafi.app';

  if (loading) {
    return <SupportSkeleton />;
  }

  return (
    <>
      <PageHeader title="Help & Support" subtitle="Reach us on Telegram or email" />
      <motion.section
        className="panel panel--glow panel--tint-cyan"
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="eyebrow">
          <HiOutlineSupport style={{ marginRight: 4 }} />
          Support
        </div>
        <h2 style={{ marginBottom: '0.35rem' }}>How can we help?</h2>
        <p className="muted" style={{ marginTop: 0, maxWidth: '36rem', lineHeight: 1.55 }}>
          Need help with wallet connect, plans, or claims? Reach our team on Telegram or email —
          we typically reply within one business day. Browse FAQs below for quick answers.
        </p>
      </motion.section>

      <div className="support-grid">
        <motion.a
          className="support-card"
          href={telegram}
          target="_blank"
          rel="noreferrer"
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.06 }}
          whileHover={{ y: -3 }}
        >
          <span className="support-card__icon" style={{ color: '#2AABEE' }}>
            <FaTelegramPlane size={26} />
          </span>
          <div>
            <h3>Telegram</h3>
            <p>{telegram.replace(/^https?:\/\//i, '')}</p>
            <span className="support-card__action">
              Open Telegram <HiOutlineExternalLink />
            </span>
          </div>
        </motion.a>

        <motion.a
          className="support-card"
          href={`mailto:${email}`}
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.12 }}
          whileHover={{ y: -3 }}
        >
          <span className="support-card__icon" style={{ color: 'var(--cyan)' }}>
            <HiOutlineMail size={26} />
          </span>
          <div>
            <h3>Gmail / Email</h3>
            <p>{email}</p>
            <span className="support-card__action">
              Send email <HiOutlineExternalLink />
            </span>
          </div>
        </motion.a>
      </div>

      <motion.section
        className="panel"
        style={{ marginTop: '1rem' }}
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.14 }}
      >
        <div className="eyebrow">
          <HiOutlineQuestionMarkCircle style={{ marginRight: 4 }} />
          FAQ
        </div>
        <h3 style={{ marginTop: 0 }}>Frequently asked questions</h3>
        <div className="faq-list">
          {FAQS.map((item, i) => (
            <FaqItem
              key={item.q}
              item={item}
              open={openFaq === i}
              onToggle={() => setOpenFaq((prev) => (prev === i ? -1 : i))}
            />
          ))}
        </div>
      </motion.section>
    </>
  );
}
